import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Download, Send, Edit2, CheckCircle, Clock, 
  AlertCircle, Printer, MoreVertical, ArrowRight, Trash2,
  CreditCard, Banknote, Building, Mail, X, XCircle, Ban, Coins,
  Shield, Pen, Smartphone, FileCheck, RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import InvoiceWarrantySignatureModal from '@/components/InvoiceWarrantySignatureModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function InvoiceDetailPage({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const invoiceRef = useRef(null);
  const downloadTriggered = useRef(false);
  
  const [invoice, setInvoice] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showWarrantySignatureModal, setShowWarrantySignatureModal] = useState(false);
  const [warrantyStatus, setWarrantyStatus] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method: 'cash',
    amount: 0,
    isPartial: false
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchInvoice();
    fetchCompanyInfo();
    fetchCompanySettings();
  }, [id]);

  // Charger le statut de signature garantie
  useEffect(() => {
    if (invoice?.id) {
      fetchWarrantyStatus();
    }
  }, [invoice?.id]);

  const fetchWarrantyStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/invoices/${id}/warranty/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWarrantyStatus(response.data);
    } catch (error) {
      // Pas d'erreur si le statut n'existe pas encore
      setWarrantyStatus({ signed: false });
    }
  };

  const handleSendWithWarranty = async () => {
    if (!warrantyStatus?.signed) {
      toast.error('La facture doit être signée avant l\'envoi avec garantie');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(
        `${BACKEND_URL}/api/invoices/${id}/warranty/send-with-signature`,
        {
          to_email: invoice.client_email,
          subject: `Facture avec Garantie - ${invoice.number || invoice.invoice_number}`,
          message: 'Veuillez trouver ci-joint votre facture avec les conditions de garantie signées.'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Facture avec garantie envoyée par email');
    } catch (error) {
      console.error('Error sending warranty invoice:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handlePrintWithWarranty = async () => {
    if (!warrantyStatus?.signed) {
      toast.error('La facture doit être signée avant l\'impression avec garantie');
      return;
    }
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/invoices/${id}/warranty/print-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ouvrir une nouvelle fenêtre d'impression avec les données
      const printWindow = window.open('', '_blank');
      printWindow.document.write(generateWarrantyPrintHTML(response.data));
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error('Error printing warranty invoice:', error);
      toast.error('Erreur lors de l\'impression');
    }
  };

  const generateWarrantyPrintHTML = (data) => {
    const { invoice: inv, company, warranty_signature, warranty_signature_date, warranty_signature_method } = data;
    const methodLabel = warranty_signature_method === 'on_site' ? 'Sur place' : 'À distance (OTP)';
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Facture avec Garantie - ${inv.number || inv.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .badge { background: #dbeafe; color: #1e40af; padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f8fafc; }
          .conditions { background: #f0f9ff; border: 2px solid #0284c7; border-radius: 8px; padding: 15px; margin: 20px 0; }
          .conditions h3 { color: #0369a1; margin: 0 0 10px 0; }
          .signature-box { background: #f0fdf4; border: 2px solid #22c55e; border-radius: 8px; padding: 15px; margin: 20px 0; }
          .signature-box h3 { color: #166534; margin: 0 0 10px 0; }
          .signature-img { max-width: 200px; border: 1px solid #ddd; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 style="margin: 0;">${company.name}</h1>
            <p>${company.address}<br>${company.postal_code} ${company.city}<br>Tél: ${company.phone}</p>
          </div>
          <div style="text-align: right;">
            <h2 style="margin: 0;">FACTURE</h2>
            <span class="badge">AVEC GARANTIE</span>
            <p style="font-size: 18px; font-family: monospace;">${inv.number || inv.invoice_number}</p>
          </div>
        </div>
        
        <div style="background: #f8fafc; padding: 10px; border-radius: 8px; margin-bottom: 20px;">
          <strong>Client:</strong> ${inv.client_name}<br>
          ${inv.client_phone ? `Tél: ${inv.client_phone}<br>` : ''}
          ${inv.client_email ? `Email: ${inv.client_email}` : ''}
        </div>
        
        <table>
          <thead>
            <tr><th>Description</th><th>Qté</th><th>Prix HT</th><th>TVA</th><th>Total HT</th></tr>
          </thead>
          <tbody>
            ${(inv.items || []).map(item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${item.unit_price?.toFixed(2)} €</td>
                <td>${item.tva_rate || 20}%</td>
                <td>${(item.quantity * item.unit_price * (1 - (item.discount || 0) / 100)).toFixed(2)} €</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="text-align: right; margin: 20px 0;">
          <p>Total HT: <strong>${(inv.total_ht || inv.subtotal || 0).toFixed(2)} €</strong></p>
          <p>TVA: <strong>${(inv.total_tva || inv.tax_amount || 0).toFixed(2)} €</strong></p>
          <p style="font-size: 16px;">Total TTC: <strong>${(inv.total_ttc || inv.total || 0).toFixed(2)} €</strong></p>
        </div>
        
        <div class="conditions">
          <h3>📋 CONDITIONS DE GARANTIE</h3>
          <p><strong>1. RÉFÉRENTIEL:</strong> 24 mois (Appareils), 12 mois (Pièces Origine), 6 mois (Premium), 3 mois (Entrée gamme)</p>
          <p><strong>2. PRIS EN CHARGE:</strong> Vices de fabrication et défauts internes uniquement</p>
          <p><strong>3. EXCLUS:</strong> Choc, oxydation, intervention tierce, pièce démontée, sticker rompu</p>
          <p><strong>4. CLAUSES:</strong> Pièce DOIT rester montée. Client responsable de la sauvegarde des données.</p>
        </div>
        
        <div class="signature-box">
          <h3>✅ LU ET APPROUVÉ</h3>
          <p><strong>Date:</strong> ${warranty_signature_date?.slice(0, 19)?.replace('T', ' ') || 'N/A'}</p>
          <p><strong>Méthode:</strong> ${methodLabel}</p>
          ${warranty_signature ? `<p><strong>Signature:</strong><br><img src="${warranty_signature}" class="signature-img" /></p>` : ''}
        </div>
        
        <div style="text-align: center; margin-top: 30px; font-size: 10px; color: #666;">
          ${company.legal_name || ''} - SIRET: ${company.siret || ''} - TVA: ${company.tva_number || ''}
        </div>
      </body>
      </html>
    `;
  };

  // Auto-download PDF if ?download=true
  useEffect(() => {
    if (invoice && searchParams.get('download') === 'true' && !downloadTriggered.current) {
      downloadTriggered.current = true;
      // Attendre que le DOM soit rendu
      setTimeout(() => {
        generatePDF();
      }, 1000);
    }
  }, [invoice, searchParams]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoice(response.data);
    } catch (error) {
      toast.error('Facture non trouvée');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Brouillon', class: 'bg-slate-100 text-slate-600', icon: Edit2 },
      sent: { label: 'Envoyée', class: 'bg-blue-100 text-blue-600', icon: Send },
      paid: { label: 'Payée', class: 'bg-green-100 text-green-600', icon: CheckCircle },
      partial: { label: 'Partiel', class: 'bg-amber-100 text-amber-600', icon: Clock },
      overdue: { label: 'En retard', class: 'bg-red-100 text-red-600', icon: AlertCircle },
      cancelled: { label: 'Annulée', class: 'bg-slate-100 text-slate-400', icon: Trash2 },
      converted: { label: 'Convertie', class: 'bg-purple-100 text-purple-600', icon: ArrowRight }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.class}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const updateStatus = async (newStatus, paymentInfo = null) => {
    try {
      const data = { status: newStatus };
      if (paymentInfo) {
        data.payment_method = paymentInfo.method;
        data.amount_paid = paymentInfo.amount;
        data.paid_date = paymentInfo.date;
      }

      await axios.patch(`${BACKEND_URL}/api/invoices/${id}/status`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Statut mis à jour');
      fetchInvoice();
      setShowStatusMenu(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const convertToInvoice = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/invoices/${id}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis converti en facture');
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const generatePDF = async () => {
    if (!invoiceRef.current) return;
    
    setExporting(true);
    toast.info('Génération du PDF...');

    try {
      const element = invoiceRef.current;
      
      // Appliquer un style temporaire pour le format A4
      const originalStyle = element.style.cssText;
      element.style.width = '210mm';
      element.style.minHeight = '297mm';
      element.style.padding = '15mm';
      element.style.boxSizing = 'border-box';
      element.style.backgroundColor = '#ffffff';
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794, // A4 width in pixels at 96 DPI
        windowWidth: 794
      });
      
      // Restaurer le style original
      element.style.cssText = originalStyle;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const imgWidth = pdfWidth - 20; // Marges de 10mm de chaque côté
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Centrer l'image
      const xOffset = 10;
      const yOffset = 10;

      // Si le contenu dépasse une page, on le met à l'échelle
      if (imgHeight > pdfHeight - 20) {
        const scale = (pdfHeight - 20) / imgHeight;
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        const centeredX = (pdfWidth - scaledWidth) / 2;
        pdf.addImage(imgData, 'PNG', centeredX, yOffset, scaledWidth, scaledHeight);
      } else {
        pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);
      }

      const fileName = `${invoice.invoice_number || invoice.number || 'facture'}.pdf`;
      pdf.save(fileName);
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setExporting(false);
    }
  };

  const openEmailModal = () => {
    setEmailRecipient(invoice.client_email || '');
    setShowEmailModal(true);
  };

  const sendByEmail = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast.error('Veuillez saisir une adresse email valide');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/invoices/${invoice.id}/send-email`,
        { to_email: emailRecipient },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        await updateStatus('sent');
        toast.success(`${invoice.invoice_type === 'invoice' ? 'Facture' : 'Devis'} envoyé(e) à ${emailRecipient}`);
        setShowEmailModal(false);
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setSending(false);
    }
  };

  // Annuler la facture
  const cancelInvoice = async () => {
    if (!cancelReason.trim()) {
      toast.error('Veuillez indiquer le motif d\'annulation');
      return;
    }

    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/invoices/${invoice.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`${typeLabel} annulé(e) avec succès`);
        setShowCancelModal(false);
        setCancelReason('');
        fetchInvoice();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  if (!invoice) return null;

  const isQuote = invoice.invoice_type === 'quote';
  const typeLabel = isQuote ? 'Devis' : 'Facture';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/invoices')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900">
                {invoice.invoice_number || invoice.number || '—'}
              </h1>
              <p className="text-sm text-slate-500">{invoice.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(invoice.status)}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Preview */}
          <div className="lg:col-span-2">
            <div 
              ref={invoiceRef}
              className="bg-white rounded-xl border-2 border-black p-8 shadow-lg invoice-print"
              style={{ 
                minHeight: '800px',
                maxWidth: '210mm',
                margin: '0 auto',
                fontSize: '14px',
                lineHeight: '1.4'
              }}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  {companySettings?.logo_url ? (
                    <img 
                      src={`${BACKEND_URL}${companySettings.logo_url}`} 
                      alt="Logo" 
                      className="w-20 h-20 object-contain"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building className="w-10 h-10 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{companyInfo?.name || 'MPHONE'}</h2>
                    <p className="text-sm text-gray-700">{companyInfo?.legal_name}</p>
                    <div className="mt-2 text-sm text-gray-800">
                      <p>{companyInfo?.address}</p>
                      <p>{companyInfo?.postal_code} {companyInfo?.city}</p>
                      <p>Tél : {companyInfo?.phone}</p>
                      <p>Email : {companyInfo?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-gray-900">{typeLabel.toUpperCase()}</h1>
                  <p className="text-xl font-mono text-gray-800 mt-1">{invoice.invoice_number || invoice.number || '—'}</p>
                  <p className="text-sm text-gray-700 mt-2">
                    Date : {invoice.issue_date || (invoice.created_at ? invoice.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10))}
                  </p>
                  {invoice.due_date && (
                    <p className="text-sm text-gray-700">Échéance : {invoice.due_date}</p>
                  )}
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-8">
                <p className="text-xs text-gray-700 mb-1 font-medium">FACTURER À</p>
                <p className="font-semibold text-gray-900">{invoice.client_name}</p>
                {invoice.client_address && <p className="text-sm text-gray-800">{invoice.client_address}</p>}
                {(invoice.client_postal_code || invoice.client_city) && (
                  <p className="text-sm text-gray-800">
                    {invoice.client_postal_code} {invoice.client_city}
                  </p>
                )}
                {invoice.client_phone && <p className="text-sm text-gray-800">Tél : {invoice.client_phone}</p>}
                {invoice.client_email && <p className="text-sm text-gray-800">{invoice.client_email}</p>}
                {invoice.client_siret && <p className="text-sm text-gray-700">SIRET : {invoice.client_siret}</p>}
                {invoice.client_tva_number && <p className="text-sm text-gray-700">TVA : {invoice.client_tva_number}</p>}
              </div>

              {/* Items Table */}
              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b-2 border-gray-800">
                    <th className="text-left py-3 text-sm font-bold text-gray-900">Description</th>
                    <th className="text-center py-3 text-sm font-bold text-gray-900 w-20">Qté</th>
                    <th className="text-right py-3 text-sm font-bold text-gray-900 w-28">Prix HT</th>
                    <th className="text-right py-3 text-sm font-bold text-gray-900 w-20">TVA</th>
                    <th className="text-right py-3 text-sm font-bold text-gray-900 w-28">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => {
                    const itemTotal = item.quantity * item.unit_price * (1 - (item.discount || 0) / 100);
                    return (
                      <tr key={index} className="border-b border-gray-300">
                        <td className="py-3 text-sm text-gray-900">{item.description}</td>
                        <td className="py-3 text-sm text-gray-800 text-center">{item.quantity}</td>
                        <td className="py-3 text-sm text-gray-800 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="py-3 text-sm text-gray-800 text-right">{item.tva_rate}%</td>
                        <td className="py-3 text-sm text-gray-900 text-right font-semibold">{formatCurrency(itemTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-gray-800 font-medium">Total HT</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(invoice.total_ht)}</span>
                  </div>
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-gray-800 font-medium">TVA (20%)</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(invoice.total_tva)}</span>
                  </div>
                  <div className="flex justify-between py-3 text-lg font-bold border-t-2 border-black mt-2">
                    <span className="text-gray-900">Total TTC</span>
                    <span className="text-gray-900">{formatCurrency(invoice.total_ttc)}</span>
                  </div>
                  
                  {/* Remise fidélité si appliquée */}
                  {invoice.loyalty_discount > 0 && (
                    <div className="flex justify-between py-1 text-sm text-amber-700">
                      <span className="flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        Remise fidélité ({invoice.loyalty_points_used} pts)
                      </span>
                      <span>-{invoice.loyalty_discount?.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  {/* Mention PAYÉ visible - Grand et gras pour impression */}
                  {invoice.status === 'paid' && (
                    <div className="mt-6 py-4 px-6 bg-green-500 text-white text-center rounded-lg print:bg-green-600">
                      <span className="text-3xl font-black tracking-wide">✓ PAYÉ</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method - Show only selected payment method */}
              {invoice.payment_method && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-gray-700 mb-2 font-medium">MODE DE RÈGLEMENT</p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-900">
                      {invoice.payment_method === 'Carte bancaire' && <CreditCard className="w-4 h-4 mr-2" />}
                      {invoice.payment_method === 'Espèces' && <Banknote className="w-4 h-4 mr-2" />}
                      {invoice.payment_method === 'Mixte' && <><CreditCard className="w-4 h-4 mr-1" /><Banknote className="w-4 h-4 mr-2" /></>}
                      {invoice.payment_method}
                    </span>
                  </div>
                  {invoice.payment_method === 'Virement bancaire' && companySettings?.bank_details?.iban && (
                    <div className="mt-3 text-sm text-gray-800">
                      <p><span className="font-semibold">Banque:</span> {companySettings.bank_details.bank_name}</p>
                      <p><span className="font-semibold">IBAN:</span> {companySettings.bank_details.iban}</p>
                      {companySettings.bank_details.bic && (
                        <p><span className="font-semibold">BIC:</span> {companySettings.bank_details.bic}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Notes & Terms */}
              {(invoice.notes || invoice.terms) && (
                <div className="mt-8 pt-8 border-t border-gray-400">
                  {invoice.terms && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-700 mb-1 font-medium">CONDITIONS</p>
                      <p className="text-sm text-gray-800">{invoice.terms}</p>
                    </div>
                  )}
                  {invoice.notes && (
                    <div>
                      <p className="text-xs text-gray-700 mb-1 font-medium">NOTES</p>
                      <p className="text-sm text-gray-800">{invoice.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* CGV */}
              {companyInfo?.cgv && (
                <div className="mt-8 pt-6 border-t border-gray-400 text-xs text-gray-700">
                  <p className="font-bold text-gray-900 mb-2">CONDITIONS GÉNÉRALES</p>
                  <div className="space-y-2">
                    <p><span className="font-semibold">Paiement:</span> {companyInfo.cgv.paiement}</p>
                    <p><span className="font-semibold">Retard:</span> {companyInfo.cgv.retard_paiement}</p>
                    <div>
                      <p className="font-semibold">Garanties:</p>
                      <ul className="list-disc list-inside ml-2">
                        {companyInfo.cgv.garanties?.slice(0, 3).map((g, i) => (
                          <li key={i}>{g}</li>
                        ))}
                      </ul>
                    </div>
                    <p><span className="font-semibold">Données:</span> {companyInfo.cgv.donnees_personnelles}</p>
                  </div>
                </div>
              )}

              {/* Solde points fidélité - Compact, en bas de la facture, optionnel */}
              {invoice.show_points_on_invoice && invoice.client_points_balance !== null && (
                <div className="mt-4 py-2 px-3 bg-amber-50 border border-amber-200 rounded flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-amber-700">
                    <Coins className="w-4 h-4" />
                    Solde fidélité
                  </span>
                  <span className="font-bold text-amber-700">{invoice.client_points_balance} points</span>
                </div>
              )}

              {/* Footer - Company Legal Info */}
              <div className="mt-8 pt-4 border-t border-gray-400 text-center text-xs text-gray-600">
                <p>{companyInfo?.legal_name} - {companyInfo?.legal_form}</p>
                <p>SIRET : {companyInfo?.siret} - TVA : {companyInfo?.tva_number} - RCS {companyInfo?.rcs}</p>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Actions</h3>
              <div className="space-y-2">
                {/* Message si facture garantie non signée */}
                {warrantyStatus?.is_warranty_invoice && !warrantyStatus?.signed && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Facture avec garantie : signature requise avant téléchargement/envoi</span>
                    </p>
                  </div>
                )}
                
                {invoice.pdf_path && (
                  <Button 
                    onClick={() => window.open(`${BACKEND_URL}/api/uploads/${invoice.pdf_path}`, '_blank')}
                    variant="outline" 
                    className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="view-original-pdf-btn"
                    disabled={warrantyStatus?.is_warranty_invoice && !warrantyStatus?.signed}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Voir PDF Original (Tiime)
                  </Button>
                )}
                
                <Button 
                  onClick={generatePDF} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={exporting || (warrantyStatus?.is_warranty_invoice && !warrantyStatus?.signed)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Génération...' : 'Télécharger PDF'}
                </Button>
                
                <Button 
                  onClick={openEmailModal} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={sending || (warrantyStatus?.is_warranty_invoice && !warrantyStatus?.signed)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Envoi...' : 'Envoyer par email'}
                </Button>

                <Button 
                  onClick={() => window.print()} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={warrantyStatus?.is_warranty_invoice && !warrantyStatus?.signed}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer
                </Button>

                {isQuote && invoice.status !== 'converted' && (
                  <Button 
                    onClick={convertToInvoice} 
                    className="w-full justify-start bg-green-600 hover:bg-green-700"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Convertir en facture
                  </Button>
                )}
              </div>
            </div>

            {/* Status Update */}
            {!isQuote && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Statut du paiement</h3>
                <div className="space-y-2">
                  {invoice.status !== 'paid' && (
                    <Button 
                      onClick={() => {
                        setPaymentData({
                          method: 'cash',
                          amount: invoice.total_ttc - (invoice.amount_paid || 0),
                          isPartial: false
                        });
                        setShowPaymentModal(true);
                      }}
                      variant="outline"
                      className="w-full justify-start text-green-600 border-green-200 hover:bg-green-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Enregistrer un paiement
                    </Button>
                  )}
                  
                  {invoice.status === 'draft' && (
                    <Button 
                      onClick={() => updateStatus('sent')}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Marquer comme envoyée
                    </Button>
                  )}

                  {invoice.status === 'sent' && (
                    <Button 
                      onClick={() => updateStatus('overdue')}
                      variant="outline"
                      className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Marquer en retard
                    </Button>
                  )}
                </div>

                {invoice.status === 'paid' && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">Payée le {invoice.paid_date || 'N/A'}</p>
                    {invoice.payment_method && (
                      <p className="text-xs text-green-600">
                        Mode : {invoice.payment_method === 'card' ? 'Carte bancaire' : 
                               invoice.payment_method === 'cash' ? 'Espèces' : 
                               invoice.payment_method === 'transfer' ? 'Virement' : 'Chèque'}
                      </p>
                    )}
                  </div>
                )}

                {/* Affichage des acomptes */}
                {invoice.amount_paid > 0 && invoice.amount_paid < invoice.total_ttc && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-700">Paiement partiel</p>
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-amber-600">Acompte reçu:</span>
                      <span className="font-bold text-amber-700">{invoice.amount_paid?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between mt-1 text-sm">
                      <span className="text-amber-600">Reste à payer:</span>
                      <span className="font-bold text-red-600">{(invoice.total_ttc - invoice.amount_paid).toFixed(2)} €</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modification et Annulation */}
            {invoice.status !== 'cancelled' && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Modification</h3>
                <div className="space-y-2">
                  <Button 
                    onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Modifier le document
                  </Button>
                  
                  {invoice.status !== 'paid' && (
                    <Button 
                      onClick={() => setShowCancelModal(true)}
                      variant="outline"
                      className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Annuler ce document
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  {invoice.status === 'paid' 
                    ? 'Vous pouvez modifier cette facture payée. L\'historique sera conservé.'
                    : 'L\'annulation conserve le numéro de document pour la traçabilité comptable.'}
                </p>
              </div>
            )}

            {/* ===== NOUVEAU: Section Garantie & Signature ===== */}
            {invoice.status !== 'cancelled' && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-4">
                <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Garantie & Signature
                </h3>
                
                {/* Statut de signature */}
                {warrantyStatus?.signed ? (
                  <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 text-green-800">
                      <FileCheck className="w-5 h-5" />
                      <span className="font-medium">Signé</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      {warrantyStatus.signature_date?.slice(0, 19)?.replace('T', ' ')}
                      <br />
                      Méthode: {warrantyStatus.signature_method === 'on_site' ? 'Sur place' : 'À distance (OTP)'}
                    </p>
                  </div>
                ) : warrantyStatus?.signature_pending ? (
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Smartphone className="w-5 h-5" />
                      <span className="font-medium">En attente de signature</span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      Un lien de signature a été envoyé au client
                    </p>
                    <Button 
                      onClick={() => setShowWarrantySignatureModal(true)}
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full border-amber-400 text-amber-700 hover:bg-amber-50"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Relancer la demande de signature
                    </Button>
                  </div>
                ) : (
                  <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-slate-600">
                      Aucune signature de garantie pour cette facture
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  {/* Bouton signature */}
                  {!warrantyStatus?.signed && (
                    <Button 
                      onClick={() => setShowWarrantySignatureModal(true)}
                      variant="outline"
                      className="w-full justify-start border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Pen className="w-4 h-4 mr-2" />
                      Signature Garantie
                    </Button>
                  )}
                  
                  {/* Boutons d'envoi/impression AVEC signature (actifs seulement si signé) */}
                  {warrantyStatus?.signed && (
                    <>
                      <Button 
                        onClick={handleSendWithWarranty}
                        variant="outline"
                        className="w-full justify-start border-green-300 text-green-700 hover:bg-green-50"
                        disabled={sending || !invoice.client_email}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {sending ? 'Envoi...' : 'Envoyer AVEC signature'}
                      </Button>
                      
                      <Button 
                        onClick={handlePrintWithWarranty}
                        variant="outline"
                        className="w-full justify-start border-green-300 text-green-700 hover:bg-green-50"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimer AVEC signature
                      </Button>
                    </>
                  )}
                </div>
                
                <p className="text-xs text-blue-600 mt-3">
                  Pour les ventes/réparations avec garantie. Les conditions du cahier des charges seront incluses.
                </p>
              </div>
            )}

            {/* Badge Annulé */}
            {invoice.status === 'cancelled' && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5" />
                  <h3 className="font-semibold">Document annulé</h3>
                </div>
                {invoice.cancel_reason && (
                  <p className="text-sm text-red-600 mt-2">Motif : {invoice.cancel_reason}</p>
                )}
                {invoice.cancelled_at && (
                  <p className="text-xs text-red-500 mt-1">Annulé le {invoice.cancelled_at.slice(0, 10)}</p>
                )}
              </div>
            )}

            {/* Invoice Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Informations</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Type</span>
                  <span className="font-medium">{typeLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Numéro</span>
                  <span className="font-medium font-mono">{invoice.invoice_number || invoice.number || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date d'émission</span>
                  <span className="font-medium">{invoice.issue_date}</span>
                </div>
                {invoice.due_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Échéance</span>
                    <span className="font-medium">{invoice.due_date}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t">
                  <span className="text-slate-500">Montant TTC</span>
                  <span className="font-bold text-lg">{formatCurrency(invoice.total_ttc)}</span>
                </div>
              </div>
            </div>

            {/* Delete (only draft) */}
            {invoice.status === 'draft' && (
              <Button 
                onClick={async () => {
                  if (window.confirm('Supprimer ce brouillon ?')) {
                    try {
                      await axios.delete(`${BACKEND_URL}/api/invoices/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Document supprimé');
                      navigate('/invoices');
                    } catch (error) {
                      toast.error('Erreur lors de la suppression');
                    }
                  }
                }}
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer le brouillon
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Modal de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Enregistrer un paiement</h2>
            
            {/* Résumé facture */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Facture</span>
                <span className="font-mono font-medium">{invoice.invoice_number || invoice.number || '—'}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Total TTC</span>
                <span className="font-bold">{invoice.total_ttc?.toFixed(2)} €</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between text-sm mt-1 text-amber-600">
                  <span>Déjà payé</span>
                  <span>{invoice.amount_paid?.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-sm mt-1 border-t pt-1">
                <span className="text-slate-600">Reste à payer</span>
                <span className="font-bold text-red-600">{(invoice.total_ttc - (invoice.amount_paid || 0)).toFixed(2)} €</span>
              </div>
            </div>

            {/* Mode de paiement */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Mode de paiement</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'cash', label: 'Espèces', icon: Banknote },
                  { value: 'card', label: 'Carte bancaire', icon: CreditCard },
                  { value: 'transfer', label: 'Virement', icon: Building },
                  { value: 'check', label: 'Chèque', icon: CreditCard }
                ].map(method => (
                  <button
                    key={method.value}
                    onClick={() => setPaymentData({...paymentData, method: method.value})}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      paymentData.method === method.value 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <method.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Type de paiement */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentData.isPartial}
                  onChange={(e) => setPaymentData({...paymentData, isPartial: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Paiement partiel (acompte)</span>
              </label>
            </div>

            {/* Montant */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {paymentData.isPartial ? 'Montant de l\'acompte' : 'Montant'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                  disabled={!paymentData.isPartial}
                  className={`w-full px-4 py-2 border rounded-lg text-lg font-bold ${
                    paymentData.isPartial 
                      ? 'bg-white border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' 
                      : 'bg-slate-100 border-slate-200 text-slate-500'
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">€</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowPaymentModal(false)}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={async () => {
                  const totalPaid = (invoice.amount_paid || 0) + paymentData.amount;
                  const newStatus = totalPaid >= invoice.total_ttc ? 'paid' : 'sent';
                  
                  await updateStatus(newStatus, {
                    method: paymentData.method,
                    amount: totalPaid,
                    date: format(new Date(), 'yyyy-MM-dd')
                  });
                  
                  setShowPaymentModal(false);
                  toast.success(totalPaid >= invoice.total_ttc ? 'Facture payée !' : 'Acompte enregistré');
                }}
                className="flex-1 bg-green-500 hover:bg-green-600"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'envoi par email */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Envoyer par email</h2>
              <button 
                onClick={() => setShowEmailModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Résumé document */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Document</span>
                <span className="font-mono font-medium">{invoice.invoice_number || invoice.number || '—'}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Type</span>
                <span className="font-medium">{invoice.invoice_type === 'invoice' ? 'Facture' : 'Devis'}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Client</span>
                <span className="font-medium">{invoice.client_name}</span>
              </div>
              <div className="flex justify-between text-sm mt-1 border-t pt-1">
                <span className="text-slate-600">Montant TTC</span>
                <span className="font-bold">{formatCurrency(invoice.total_ttc)}</span>
              </div>
            </div>

            {/* Email destinataire */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Adresse email du destinataire
              </label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="client@exemple.com"
                className="w-full"
                autoFocus
              />
              {invoice.client_email && invoice.client_email !== emailRecipient && (
                <button 
                  onClick={() => setEmailRecipient(invoice.client_email)}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                >
                  Utiliser l'email du client: {invoice.client_email}
                </button>
              )}
            </div>

            {/* Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Le document sera envoyé au format PDF en pièce jointe.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowEmailModal(false)}
                variant="outline"
                className="flex-1"
                disabled={sending}
              >
                Annuler
              </Button>
              <Button
                onClick={sendByEmail}
                disabled={sending || !emailRecipient}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'annulation */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Annuler ce document
              </h2>
              <button 
                onClick={() => setShowCancelModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Info document */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Document</span>
                <span className="font-mono font-medium">{invoice.invoice_number || invoice.number || '—'}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Client</span>
                <span className="font-medium">{invoice.client_name}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Montant</span>
                <span className="font-bold">{formatCurrency(invoice.total_ttc)}</span>
              </div>
            </div>

            {/* Avertissement */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-700">
                <strong>Important :</strong> L'annulation est définitive. Le numéro de document sera conservé pour la traçabilité comptable.
              </p>
            </div>

            {/* Motif d'annulation */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Motif d'annulation *
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Erreur de facturation, demande du client, doublon..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowCancelModal(false)}
                variant="outline"
                className="flex-1"
              >
                Retour
              </Button>
              <Button
                onClick={cancelInvoice}
                disabled={!cancelReason.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                <Ban className="w-4 h-4 mr-2" />
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Signature Garantie */}
      <InvoiceWarrantySignatureModal
        isOpen={showWarrantySignatureModal}
        onClose={() => setShowWarrantySignatureModal(false)}
        invoice={invoice}
        onSignatureComplete={() => {
          fetchWarrantyStatus();
          toast.success('Signature garantie enregistrée');
        }}
      />
    </div>
  );
}
